/**
 * GRIDHEIMR — Bot Metrics Component
 *
 * Displays resource utilization metrics for a bot instance.
 */

import React from "react";
import { useMetrics } from "../hooks/useMetrics";
import { MetricsChart } from "./MetricsChart";
import { BLUE, YELLOW, GREEN, DARK_BG, PIXEL, MONO } from "../theme";

/**
 * Component props
 */
interface BotMetricsProps {
  /**
   * Bot ID to fetch metrics for
   */
  botId: string;
}

/**
 * Format bytes to human-readable string
 */
function formatBytes(bytes: number): { value: number; unit: string } {
  if (bytes === 0) return { value: 0, unit: "B" };

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return {
    value: parseFloat((bytes / Math.pow(k, i)).toFixed(2)),
    unit: sizes[i],
  };
}

/**
 * Format uptime seconds to human-readable string
 */
function formatUptime(seconds: number | null): string {
  if (seconds === null || seconds === 0) return "Not running";

  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(" ");
}

/**
 * Bot Metrics Component
 *
 * Fetches and displays real-time metrics for a bot instance.
 */
export function BotMetrics({ botId }: BotMetricsProps) {
  const { metrics, loading, error } = useMetrics(botId);

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>📊 Resource Metrics</h2>
        </div>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}>🔄</div>
          <p style={styles.loadingText}>Loading metrics...</p>
        </div>
      </div>
    );
  }

  /**
   * Render error state
   */
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>📊 Resource Metrics</h2>
        </div>
        <div style={styles.errorContainer}>
          <span style={styles.errorIcon}>⚠️</span>
          <p style={styles.errorText}>Failed to load metrics: {error}</p>
        </div>
      </div>
    );
  }

  /**
   * Render no data state
   */
  if (!metrics) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>📊 Resource Metrics</h2>
        </div>
        <div style={styles.emptyContainer}>
          <p style={styles.emptyText}>No metrics available</p>
        </div>
      </div>
    );
  }

  // Format network bytes
  const networkRx = formatBytes(metrics.network.rx_bytes);
  const networkTx = formatBytes(metrics.network.tx_bytes);
  const diskRead = formatBytes(metrics.disk.read_bytes);
  const diskWrite = formatBytes(metrics.disk.write_bytes);

  /**
   * Render metrics
   */
  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>📊 Resource Metrics</h2>
          <p style={styles.subtitle}>
            Last updated: {new Date(metrics.collected_at).toLocaleTimeString()}
          </p>
        </div>
        {metrics.uptime_seconds !== null && (
          <div style={styles.uptimeBadge}>
            <span style={styles.uptimeIcon}>⏱️</span>
            <span style={styles.uptimeText}>
              Uptime: {formatUptime(metrics.uptime_seconds)}
            </span>
          </div>
        )}
      </div>

      {/* Primary Metrics */}
      <div style={styles.metricsGrid}>
        {/* CPU Usage */}
        <MetricsChart
          title="CPU Usage"
          value={metrics.cpu.usage_percent}
          max={100}
          unit="%"
          icon="💻"
        />

        {/* Memory Usage */}
        <MetricsChart
          title="Memory Usage"
          value={metrics.memory.usage_mb}
          max={metrics.memory.total_mb}
          unit="MB"
          icon="🧠"
        />
      </div>

      {/* Secondary Metrics */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Network I/O</h3>
        <div style={styles.metricsGrid}>
          <MetricsChart
            title="Received"
            value={networkRx.value}
            unit={networkRx.unit}
            icon="📥"
            color={BLUE}
          />
          <MetricsChart
            title="Transmitted"
            value={networkTx.value}
            unit={networkTx.unit}
            icon="📤"
            color="#8b5cf6"
          />
        </div>
        <div style={styles.statsRow}>
          <div style={styles.statItem}>
            <span style={styles.statLabel}>RX Packets:</span>
            <span style={styles.statValue}>{metrics.network.rx_packets.toLocaleString()}</span>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statLabel}>TX Packets:</span>
            <span style={styles.statValue}>{metrics.network.tx_packets.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Disk Metrics */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Disk I/O</h3>
        <div style={styles.metricsGrid}>
          <MetricsChart
            title="Read"
            value={diskRead.value}
            unit={diskRead.unit}
            icon="📖"
            color={GREEN}
          />
          <MetricsChart
            title="Write"
            value={diskWrite.value}
            unit={diskWrite.unit}
            icon="✏️"
            color={YELLOW}
          />
        </div>
        <div style={styles.statsRow}>
          <div style={styles.statItem}>
            <span style={styles.statLabel}>Read Ops:</span>
            <span style={styles.statValue}>{metrics.disk.read_ops.toLocaleString()}</span>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statLabel}>Write Ops:</span>
            <span style={styles.statValue}>{metrics.disk.write_ops.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      {metrics.status !== "running" && (
        <div style={styles.infoBanner}>
          <span style={styles.infoIcon}>ℹ️</span>
          <span style={styles.infoText}>
            Bot is not running. Metrics may show zero values.
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Inline styles
 */
const styles: Record<string, React.CSSProperties> = {
  container: {
    width: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "1.5rem",
    paddingBottom: "1rem",
    borderBottom: `1px solid rgba(0,106,167,0.15)`,
  },
  title: {
    margin: 0,
    fontFamily: PIXEL,
    fontSize: "0.6rem",
    fontWeight: 400,
    color: YELLOW,
    letterSpacing: "0.08em",
    textShadow: `0 0 10px rgba(254,204,0,0.3)`,
  },
  subtitle: {
    margin: "0.5rem 0 0 0",
    fontFamily: MONO,
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.4)",
  },
  uptimeBadge: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.5rem 1rem",
    background: `rgba(0,106,167,0.08)`,
    border: `1px solid rgba(0,106,167,0.25)`,
  },
  uptimeIcon: {
    fontSize: "1rem",
  },
  uptimeText: {
    fontFamily: MONO,
    fontSize: "0.8rem",
    fontWeight: 500,
    color: BLUE,
  },
  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "1px",
    background: `rgba(0,106,167,0.06)`,
    border: `1px solid rgba(0,106,167,0.12)`,
    marginBottom: "1.5rem",
  },
  section: {
    marginBottom: "1.5rem",
  },
  sectionTitle: {
    margin: "0 0 1rem 0",
    fontFamily: PIXEL,
    fontSize: "0.5rem",
    fontWeight: 400,
    color: YELLOW,
    letterSpacing: "0.1em",
    textShadow: `0 0 8px rgba(254,204,0,0.3)`,
  },
  statsRow: {
    display: "flex",
    justifyContent: "space-around",
    gap: "1rem",
    marginTop: "1rem",
    padding: "1rem",
    background: DARK_BG,
    border: `1px solid rgba(0,106,167,0.12)`,
  },
  statItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.25rem",
  },
  statLabel: {
    fontFamily: PIXEL,
    fontSize: "0.35rem",
    color: "rgba(255,255,255,0.4)",
    letterSpacing: "0.08em",
  },
  statValue: {
    fontFamily: MONO,
    fontSize: "1rem",
    fontWeight: 600,
    color: "rgba(255,255,255,0.8)",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "3rem",
    gap: "1rem",
  },
  spinner: {
    fontSize: "2rem",
    animation: "spin 1s linear infinite",
  },
  loadingText: {
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.5)",
    margin: 0,
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "2rem",
    gap: "0.5rem",
    background: "rgba(239,68,68,0.08)",
    border: "1px solid rgba(239,68,68,0.25)",
  },
  errorIcon: {
    fontSize: "2rem",
  },
  errorText: {
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "#fca5a5",
    margin: 0,
    textAlign: "center",
  },
  emptyContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "3rem",
  },
  emptyText: {
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.3)",
    margin: 0,
  },
  infoBanner: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    background: `rgba(0,106,167,0.08)`,
    border: `1px solid rgba(0,106,167,0.25)`,
    padding: "1rem",
    marginTop: "1.5rem",
  },
  infoIcon: {
    fontSize: "1.25rem",
  },
  infoText: {
    flex: 1,
    fontFamily: MONO,
    fontSize: "0.8rem",
    color: BLUE,
  },
};
